/**
 * OnboardingPage - Main onboarding flow orchestrator
 * Now uses extracted components and OnboardingContext for state management
 */

import React, { useState, useEffect } from 'react';
import { OnboardingProvider, useOnboarding } from '../contexts/OnboardingContext';
import WelcomeScreen from '../components/onboarding/WelcomeScreen';
import DataHubScreen from '../components/onboarding/DataHubScreen';
import AIQuickReview from '../components/onboarding/AIQuickReview';
import PlatformReadyScreen from '../components/onboarding/PlatformReadyScreen';
import DataSourceWizard from '../components/onboarding/DataSourceWizard';
import { useOAuth } from '../hooks/useOAuth';

// Main Onboarding Flow Component
const OnboardingFlowContent: React.FC = () => {
  const { 
    currentScreen, 
    setCurrentScreen, 
    selectedSources, 
    setSelectedSources,
    connectedSources,
    setConnectedSources 
  } = useOnboarding();
  
  // Wizard state
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [wizardQueue, setWizardQueue] = useState<string[]>([]);
  const [currentWizardStep, setCurrentWizardStep] = useState(0);

  // OAuth integration
  const { isAuthenticated: isOAuthConnected } = useOAuth();

  // Sync OAuth connection status with onboarding state
  useEffect(() => {
    if (isOAuthConnected && !connectedSources.includes('email')) {
      setConnectedSources([...connectedSources, 'email']);
    } else if (!isOAuthConnected && connectedSources.includes('email')) {
      setConnectedSources(connectedSources.filter(source => source !== 'email'));
    }
  }, [isOAuthConnected, connectedSources, setConnectedSources]);

  const startWizardQueue = () => {
    setWizardQueue([...selectedSources]);
    setCurrentWizardStep(0);
    setShowSidePanel(true);
  };

  const nextWizardStep = () => {
    const currentSource = wizardQueue[currentWizardStep];
    if (currentSource) {
      setConnectedSources([...connectedSources, currentSource]);
    }
    
    if (currentWizardStep + 1 >= wizardQueue.length) {
      setShowSidePanel(false);
      setCurrentScreen('quick-review');
    } else {
      setCurrentWizardStep(currentWizardStep + 1);
    }
  };

  const skipWizardStep = () => {
    if (currentWizardStep + 1 >= wizardQueue.length) {
      setShowSidePanel(false);
      setCurrentScreen('quick-review');
    } else {
      setCurrentWizardStep(currentWizardStep + 1);
    }
  };

  const closeSidePanel = () => {
    setShowSidePanel(false);
    setSelectedSources([]);
  };

  return (
    <div>
      {currentScreen === 'welcome' && <WelcomeScreen />}
      {currentScreen === 'data-hub' && (
        <DataHubScreen onStartWizard={startWizardQueue} />
      )}
      {currentScreen === 'quick-review' && <AIQuickReview />}
      {currentScreen === 'completeness' && <PlatformReadyScreen />}
      
      {/* Data Source Wizard */}
      {showSidePanel && wizardQueue.length > 0 && (
        <DataSourceWizard
          sourceId={wizardQueue[currentWizardStep]}
          isOpen={showSidePanel}
          onClose={closeSidePanel}
          onComplete={nextWizardStep}
          onSkip={skipWizardStep}
          stepNum={currentWizardStep + 1}
          totalSteps={wizardQueue.length}
          nextSourceTitle={currentWizardStep + 1 < wizardQueue.length ? wizardQueue[currentWizardStep + 1] : undefined}
        />
      )}
    </div>
  );
};

// Main exported component with context provider
const OnboardingFlow: React.FC = () => {
  return (
    <OnboardingProvider>
      <OnboardingFlowContent />
    </OnboardingProvider>
  );
};

export default OnboardingFlow;